import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Heart, ShieldCheck, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CommunityPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => api.get(`/community/posts/${postId}`).then((r) => setPost(r.data)), [postId]);

  useEffect(() => { load(); }, [load]);

  const toggleLike = async (targetId, isReply) => {
    const { data } = await api.post(`/community/posts/${targetId}/like`);
    setPost((prev) => {
      if (!isReply) return { ...prev, like_count: data.like_count, liked_by_me: data.liked_by_me };
      return { ...prev, replies: prev.replies.map((r) => (r.id === targetId ? { ...r, like_count: data.like_count, liked_by_me: data.liked_by_me } : r)) };
    });
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/community/posts/${postId}/reply`, { message: reply });
      setReply("");
      load();
    } catch (e) {
      toast.error("Couldn't post your reply");
    } finally {
      setSubmitting(false);
    }
  };

  if (!post) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="community-post-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="community-post-page">
      <Link to="/community" data-testid="community-post-back-link" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Community
      </Link>

      <div className="glass-panel rounded-2xl p-6" data-testid="community-post-detail">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-white overflow-hidden">
            {post.user_picture ? <img src={post.user_picture} alt={post.user_name} loading="lazy" className="w-full h-full object-cover" /> : post.user_name?.[0]}
          </div>
          <span className="text-sm text-white font-medium">{post.user_name}</span>
          {post.is_mentor && <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />}
          <span className="text-xs text-slate-500 ml-auto">{new Date(post.created_at).toLocaleString()}</span>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-white">{post.title}</h1>
        <p className="text-sm text-slate-300 mt-3 whitespace-pre-wrap leading-relaxed">{post.body}</p>
        <button onClick={() => toggleLike(post.id, false)} data-testid="community-post-like-btn" className={`flex items-center gap-1.5 text-xs font-semibold mt-4 ${post.liked_by_me ? "text-rose-400" : "text-slate-500 hover:text-rose-300"}`}>
          <Heart className={`w-3.5 h-3.5 ${post.liked_by_me ? "fill-rose-400" : ""}`} /> {post.like_count}
        </button>
      </div>

      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mt-8 mb-3">{post.replies.length} Replies</h2>
      <div className="space-y-3" data-testid="community-post-replies">
        {post.replies.map((r) => (
          <div key={r.id} className="glass-panel rounded-xl p-4" data-testid={`community-reply-${r.id}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-sm text-white font-medium">{r.user_name}</span>
              {r.is_mentor && <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />}
              <span className="text-xs text-slate-500 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{r.body}</p>
            <button onClick={() => toggleLike(r.id, true)} data-testid={`community-reply-like-btn-${r.id}`} className={`flex items-center gap-1.5 text-xs font-semibold mt-2 ${r.liked_by_me ? "text-rose-400" : "text-slate-500 hover:text-rose-300"}`}>
              <Heart className={`w-3 h-3 ${r.liked_by_me ? "fill-rose-400" : ""}`} /> {r.like_count}
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={submitReply} className="flex items-center gap-2 mt-5" data-testid="community-reply-form">
        <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply..." data-testid="community-reply-input" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
        <button type="submit" disabled={submitting} data-testid="community-reply-submit-btn" className="p-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
